import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowRight, BadgeCheck, CheckCircle2, Eye, Gavel, HandCoins, Inbox,
    MessageSquare, Send, TrendingUp, Users,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { MarketCard } from "@/components/MarketCard";
import { SampleBanner } from "@/components/SampleBanner";
import { fetchCreatorProfile, fetchMarket, submitMarketFeedback } from "@/lib/api";
import { money } from "@/lib/format";
import { compact, watchTime } from "@/lib/market";

const StepHead = ({ n, title, kicker }) => (
    <div className="flex items-baseline gap-4 mb-4">
        <span className="font-heading text-4xl font-bold text-brand/30 tabular shrink-0">
            {String(n).padStart(2, "0")}
        </span>
        <div>
            {kicker ? (
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{kicker}</div>
            ) : null}
            <h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>
        </div>
    </div>
);

/**
 * Single-scroll feedback walkthrough for the sponsor marketplace. Real seeded data,
 * read-only except the feedback form — a demo wallet is not required to follow it.
 */
export default function DemoMarc() {
    const creator = useQuery({
        queryKey: ["creator", "hyroxmaya"],
        queryFn: () => fetchCreatorProfile("hyroxmaya"),
        retry: false,
    });
    const market = useQuery({
        queryKey: ["market", "demo"],
        queryFn: () => fetchMarket({ niche: "HYROX" }),
        refetchInterval: 30000,
    });

    const [note, setNote] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const listings = market.data?.listings || [];
    const chestKit = listings.find((l) => /chest kit/i.test(l.title));
    const supplementSlots = listings.filter((l) =>
        (l.sponsor_fits || []).some((s) => s.category === "supplements"),
    );
    const rules = market.data?.rules;

    const send = async () => {
        if (!note.trim() || busy) return;
        setBusy(true);
        try {
            await submitMarketFeedback(note.trim(), "/demo-marc");
            setSent(true);
            toast.success("Feedback saved — thank you");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save feedback");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <div className="mb-4"><SampleBanner className="inline-flex" /></div>
            <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight">
                The connection layer you asked for.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
                Sponsors can't find the right athletes, athletes get buried in DMs, and nobody
                shares the same numbers. Here is the loop, end to end, on real seeded data —
                scroll straight through.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
                {["Problem", "Profile", "Filter", "Bid", "Outbid", "Proof", "Fee", "Your notes"].map((s, i) => (
                    <a
                        key={s}
                        href={`#step-${i + 1}`}
                        className="text-xs rounded-full border border-border bg-card px-3 py-1.5 hover:border-brand/40 hover:text-brand transition-colors"
                    >
                        {i + 1}. {s}
                    </a>
                ))}
            </div>

            <section id="step-1" className="mt-12">
                <StepHead n={1} kicker="The problem" title="Brands guess. Athletes DM. No shared stats." />
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        {
                            icon: Inbox,
                            title: "Brands guess",
                            body: "A supplement founder picks athletes by follower count and vibes, then hopes the audience matches.",
                        },
                        {
                            icon: MessageSquare,
                            title: "Athletes DM",
                            body: "Creators cold-message twenty brands a week. No rate card, no deliverable, no tracking — most threads die.",
                        },
                        {
                            icon: TrendingUp,
                            title: "No shared stats",
                            body: "There is no one place with followers, average views and watch time both sides accept as the baseline.",
                        },
                    ].map((p) => (
                        <Card key={p.title} className="bg-card border-border p-4">
                            <p.icon className="h-5 w-5 text-heat mb-2" />
                            <h3 className="font-heading font-semibold">{p.title}</h3>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.body}</p>
                        </Card>
                    ))}
                </div>
                <p className="text-sm mt-4 text-muted-foreground">
                    The fix is not another inbox. It is a public board where the <strong>connection</strong>{" "}
                    happens on the card: inventory + metrics + a price.
                </p>
            </section>

            <section id="step-2" className="mt-12">
                <StepHead n={2} kicker="The profile" title="@hyroxmaya, with numbers attached" />
                <Card className="bg-card border-border p-5" data-testid="demo-profile">
                    {creator.isLoading ? (
                        <Skeleton className="h-40 rounded-xl" />
                    ) : creator.isError ? (
                        <p className="text-sm text-muted-foreground">
                            Demo data still loading — the profile is @hyroxmaya: HYROX, Berlin,
                            84.2K followers, 31.2K avg views, 0:18 watch time.
                        </p>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-heading text-lg font-bold">{creator.data.creator.name}</span>
                                <Badge className="bg-brand/15 text-brand border-brand/30 hover:bg-brand/15">
                                    <BadgeCheck className="h-3 w-3 mr-1" /> {creator.data.creator.stats_status === "verified" ? "Verified" : "Claimed"}
                                </Badge>
                                <Badge variant="secondary">{creator.data.creator.niche}</Badge>
                                <span className="text-xs text-muted-foreground">{creator.data.creator.city}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">{creator.data.creator.bio}</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                                <div className="rounded-lg bg-surface-2 px-3 py-2">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><Users className="h-3 w-3" /> Followers</div>
                                    <div className="font-heading font-semibold tabular">{compact(creator.data.creator.followers)}</div>
                                </div>
                                <div className="rounded-lg bg-surface-2 px-3 py-2">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Avg views 30d</div>
                                    <div className="font-heading font-semibold tabular">{compact(creator.data.creator.avg_views_30d)}</div>
                                </div>
                                <div className="rounded-lg bg-surface-2 px-3 py-2">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground"><TrendingUp className="h-3 w-3 inline" /> Watch time</div>
                                    <div className="font-heading font-semibold tabular">{watchTime(creator.data.creator.avg_watch_time_sec)}</div>
                                </div>
                                <div className="rounded-lg bg-surface-2 px-3 py-2">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Next event</div>
                                    <div className="font-heading font-semibold text-sm">{creator.data.creator.next_event || "—"}</div>
                                </div>
                            </div>
                            <Button asChild variant="link" className="px-0 mt-3 text-brand text-xs">
                                <Link to="/c/hyroxmaya">Open the full profile →</Link>
                            </Button>
                        </>
                    )}
                </Card>
            </section>

            <section id="step-3" className="mt-12">
                <StepHead n={3} kicker="The filter" title="HYROX × supplements — in two clicks" />
                <p className="text-sm text-muted-foreground mb-4">
                    A supplement brand filters the board: niche <Badge variant="secondary" className="mx-1">HYROX</Badge>
                    category <Badge variant="secondary" className="mx-1">supplements</Badge>.
                    The grid returns only creators whose recent views clear the brand's floor —
                    this is the connection step that used to take a week of DMs.
                </p>
                <div className="rounded-xl border border-brand/30 bg-brand/5 p-4 text-sm">
                    <strong>{supplementSlots.length}</strong> live HYROX slots fit supplement sponsors
                    right now{rules ? "" : ""}.
                    <Button asChild variant="link" className="px-0 ml-1 text-brand text-xs">
                        <Link to="/market?niche=HYROX">Run this filter on /market →</Link>
                    </Button>
                </div>
                {market.isLoading ? (
                    <Skeleton className="h-64 rounded-2xl mt-4" />
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="demo-filtered">
                        {supplementSlots.slice(0, 4).map((listing) => (
                            <MarketCard key={listing.id} listing={listing} />
                        ))}
                    </div>
                )}
            </section>

            <section id="step-4" className="mt-12">
                <StepHead n={4} kicker="The bid" title="Bid on the chest kit — from the wallet" />
                {chestKit ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                        <MarketCard listing={chestKit} />
                        <Card className="bg-card border-border p-5">
                            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">The mechanics</div>
                            <ul className="text-sm space-y-2">
                                <li>· Bid {money(chestKit.next_bid_usd)} → {money(chestKit.next_bid_usd)} leaves "available", shows as "held".</li>
                                <li>· Nothing is charged. The hold just reserves your place at the top.</li>
                                <li>· You must beat {money(chestKit.current_bid_usd)} by at least {money(chestKit.min_increment_usd)}.</li>
                                <li>· Wallet short? The bid is rejected with a clear error and a Top-up button.</li>
                            </ul>
                            <Button asChild className="mt-4 w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                                <Link to={`/a/${chestKit.id}`}>
                                    <Gavel className="h-4 w-4 mr-1.5" /> Open this auction
                                </Link>
                            </Button>
                        </Card>
                    </div>
                ) : (
                    <Card className="bg-card border-border p-5 text-sm text-muted-foreground">
                        Load the chest kit auction directly:{" "}
                        <Link to="/market?niche=HYROX" className="text-brand">/market?niche=HYROX</Link>
                    </Card>
                )}
            </section>

            <section id="step-5" className="mt-12">
                <StepHead n={5} kicker="The outbid" title="Getting outbid releases your money instantly" />
                <Card className="bg-card border-border p-5">
                    <div className="grid sm:grid-cols-3 gap-3 text-sm">
                        <div className="rounded-lg bg-surface-2 p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">You bid</div>
                            <div className="font-heading font-semibold tabular">{money(chestKit?.current_bid_usd || 1600)}</div>
                            <div className="text-xs text-muted-foreground mt-1">held from your wallet</div>
                        </div>
                        <div className="rounded-lg bg-surface-2 p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Someone bids higher</div>
                            <div className="font-heading font-semibold tabular text-heat">+1 increment</div>
                            <div className="text-xs text-muted-foreground mt-1">they take the lead, you're outbid</div>
                        </div>
                        <div className="rounded-lg bg-brand/10 border border-brand/30 p-3">
                            <div className="text-[10px] uppercase tracking-wide text-brand">Same second</div>
                            <div className="font-heading font-semibold tabular text-brand">Hold released</div>
                            <div className="text-xs text-muted-foreground mt-1">your {money(chestKit?.current_bid_usd || 1600)} is back in available</div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                        Enforced server-side: one guarded write per bid, holds derived from active
                        bids — a released hold can never linger as "spent".
                    </p>
                </Card>
            </section>

            <section id="step-6" className="mt-12">
                <StepHead n={6} kicker="The proof" title="Deliver, upload proof, 48-hour window" />
                <Card className="bg-card border-border p-5">
                    <ul className="text-sm space-y-2.5">
                        <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                            Auction closes → the winner's hold is captured. Only the winner pays.</li>
                        <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                            Creator has <strong>7 days</strong> to upload proof:{" "}
                            <span className="text-muted-foreground">
                                {chestKit?.proof_required || "race photos + post URLs"}
                            </span>
                        </li>
                        <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                            Sponsor gets <strong>48 hours</strong> to accept or dispute — one click, no thread.</li>
                        <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                            No proof in 7 days → the winner's full amount refunds to their wallet, automatically.</li>
                    </ul>
                </Card>
            </section>

            <section id="step-7" className="mt-12">
                <StepHead n={7} kicker="The fee" title="12%, taken once, on completed deals" />
                <Card className="bg-card border-border p-5">
                    <div className="grid sm:grid-cols-3 gap-3 text-center">
                        <div className="rounded-lg bg-surface-2 p-4">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Win</div>
                            <div className="font-heading text-2xl font-bold tabular">{money(1000)}</div>
                        </div>
                        <div className="rounded-lg bg-heat/10 border border-heat/30 p-4">
                            <div className="text-[10px] uppercase tracking-wide text-heat">Platform keeps</div>
                            <div className="font-heading text-2xl font-bold tabular text-heat">{money(120)}</div>
                        </div>
                        <div className="rounded-lg bg-brand/10 border border-brand/30 p-4">
                            <div className="text-[10px] uppercase tracking-wide text-brand">Creator receives</div>
                            <div className="font-heading text-2xl font-bold tabular text-brand">{money(880)}</div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                        Bidding is free. The 12% comes out of the creator's payout at acceptance —
                        sponsors never pay above their bid.
                    </p>
                </Card>
            </section>

            <section id="step-8" className="mt-12">
                <StepHead n={8} kicker="From Marc" title="Feedback from Marc" />
                <Card className="bg-card border-border p-5" data-testid="demo-feedback">
                    <p className="text-xs text-muted-foreground mb-3">
                        Straight talk welcome — what's missing, what's confusing, what would make you
                        use this on Monday.
                    </p>
                    {sent ? (
                        <div className="rounded-lg border border-brand/30 bg-brand/10 px-3 py-3 text-sm text-brand flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" /> Saved. Thank you — noted against this walkthrough.
                        </div>
                    ) : (
                        <>
                            <Textarea
                                data-testid="demo-feedback-input"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={5}
                                placeholder="Notes…"
                            />
                            <Button
                                data-testid="demo-feedback-send"
                                onClick={send}
                                disabled={busy || !note.trim()}
                                className="mt-3 bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                            >
                                <Send className="h-4 w-4 mr-1.5" /> {busy ? "Sending…" : "Send feedback"}
                            </Button>
                        </>
                    )}
                </Card>
                <div className="flex flex-wrap gap-3 mt-6">
                    <Button asChild variant="outline" className="font-semibold">
                        <Link to="/market"><ArrowRight className="h-4 w-4 mr-1.5" /> Browse the market</Link>
                    </Button>
                    <Button asChild variant="outline" className="font-semibold">
                        <Link to="/"><HandCoins className="h-4 w-4 mr-1.5" /> Back to home</Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}




